import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  DocumentMagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { adminApi } from '../services/api';
import { Layout } from '../components/shared/Layout';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Alert } from '../components/shared/Alert';
import { AdminRequestModal } from '../components/admin/AdminRequestModal';
import type { VerificationStatus } from '../types';

const statusConfig: Record<VerificationStatus, { label: string; color: string }> = {
  Draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  PendingPayment: { label: 'Pending Payment', color: 'bg-yellow-100 text-yellow-700' },
  PaymentReceived: { label: 'Ready for Review', color: 'bg-blue-100 text-blue-700' },
  InReview: { label: 'In Review', color: 'bg-purple-100 text-purple-700' },
  Approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  Rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
  Completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  Expired: { label: 'Expired', color: 'bg-gray-100 text-gray-700' },
};

export function AdminDashboardPage() {
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => adminApi.getStats().then((res) => res.data),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const {
    data: requests,
    isLoading: requestsLoading,
    error: requestsError,
    refetch: refetchRequests,
  } = useQuery({
    queryKey: ['admin-requests'],
    queryFn: () => adminApi.getRequests().then((res) => res.data),
    refetchInterval: 30000,
  });

  const handleRequestProcessed = () => {
    setSelectedRequestId(null);
    refetchRequests();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-600 mt-1">
            Review and process verification requests
          </p>
        </div>

        {/* Stats Cards */}
        {statsLoading ? (
          <LoadingSpinner size="lg" className="py-8" />
        ) : statsError ? (
          <Alert type="error" message="Failed to load statistics" />
        ) : stats ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <ClockIcon className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Review</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingReview}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <BanknotesIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending EFT</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingEftPayments}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircleIcon className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Completed Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.completedToday}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircleIcon className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rejected Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.rejectedToday}</p>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Revenue Today</p>
                  <p className="text-2xl font-bold text-gray-900">
                    R{stats.revenueToday.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* Requests Table */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Verification Requests</h2>
            <button
              onClick={() => refetchRequests()}
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              Refresh
            </button>
          </div>

          {requestsLoading ? (
            <LoadingSpinner size="lg" className="py-8" />
          ) : requestsError ? (
            <Alert type="error" message="Failed to load requests" />
          ) : !requests || requests.length === 0 ? (
            <div className="text-center py-8">
              <DocumentMagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto" />
              <p className="mt-4 text-gray-500">No verification requests</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applicant
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID Number
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((request) => (
                    <tr key={request.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className="text-sm font-medium text-gray-900">{request.fullName}</p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <p className="text-sm text-gray-600 font-mono">{request.idNumber}</p>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                            statusConfig[request.status].color
                          }`}
                        >
                          {statusConfig[request.status].label}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {request.paymentConfirmed ? (
                          <span className="inline-flex items-center text-green-600">
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            Confirmed
                          </span>
                        ) : (
                          <span className="text-yellow-600">Pending</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(request.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right">
                        <button
                          onClick={() => setSelectedRequestId(request.id)}
                          className="inline-flex items-center text-primary-600 hover:text-primary-500"
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Request Detail Modal */}
        {selectedRequestId && (
          <AdminRequestModal
            requestId={selectedRequestId}
            onClose={() => setSelectedRequestId(null)}
            onProcessed={handleRequestProcessed}
          />
        )}
      </div>
    </Layout>
  );
}
